import { useMutation } from "@tanstack/react-query";
import { Button, Card, Form, Input, Modal } from "antd";
import { CardSize } from "antd/lib/card/Card";
import { useAuthenticatedRequestCreator } from "hooks/useRequests";
import { MaxTitleLength } from "models/cardIngestion";
import { useState } from "react";

interface RuleTestResult {
    input: string;
    output: string;
}

const useRuleTest = () => {
    const test = useAuthenticatedRequestCreator<RuleTestResult, { cardTitle: string }>(
        (data) => ({
            method: 'post',
            url: '/website-settings/card-title-rules/test',
            data
        })
    );

    const { mutateAsync: testRule, isLoading } = useMutation(test);

    return { testRule, isLoading };
}

const resultCardLayout = {
    size: "small" as CardSize,
    bordered: false,
    headStyle: {
        backgroundColor: 'rgba(from var(--dcs-warning) r g b / 0.05)'
    }
}

const TestRulesModal = () => {
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState<string>('');
    const [result, setResult] = useState<RuleTestResult>();
    const [form] = Form.useForm<{ cardTitle: string }>();
    const { testRule, isLoading } = useRuleTest();

    const closeAndReset = () => {
        setOpen(false);
        form.resetFields();
        setResult(undefined);
    }

    const submit = async () => {
        await form.validateFields();
        const result = await testRule({ cardTitle: title });
        setResult(result);
    }

    return <>
        <Button type="primary" onClick={() => setOpen(true)}>Test Rules</Button>
        <Modal
            title="Test Rules"
            width='800px'
            open={open}
            onCancel={closeAndReset}
            closable={false}
            maskClosable={false}
            destroyOnClose
            keyboard={false}
            footer={<Button onClick={closeAndReset}>Cancel</Button>}
            confirmLoading={isLoading}
        >
            <Form form={form} initialValues={{ cardTitle: '' }} disabled={isLoading}>
                <Form.Item
                    labelCol={{ span: 3 }}
                    label="Card Title"
                    name="cardTitle"
                    rules={[
                        { required: true, message: 'Card title is required' }
                    ]}>
                    <Input
                        showCount={{ formatter: (args) => `${args.count} / ${MaxTitleLength}` }}
                        onChange={e => setTitle(e.target.value)}
                        spellCheck
                    />
                </Form.Item>
                <Form.Item wrapperCol={{ offset: 3 }}>
                    <Button type="primary" onClick={submit}>Run Test</Button>
                </Form.Item>
                {result && <Form.Item
                    wrapperCol={{ offset: 3 }}
                >
                    <Card {...resultCardLayout} title={`Input - ${result.input.length}/${MaxTitleLength} characters`} >
                        {result.input}
                    </Card>
                    <Card {...resultCardLayout} title={`Result - ${result.output.length}/${MaxTitleLength} characters`} style={{ marginTop: '16px' }}>
                        {result.output}
                    </Card>
                </Form.Item>}
            </Form>
        </Modal>
    </>
};

export default TestRulesModal;